// components/AboutSection.tsx
import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import wedImg3 from "../../assets/wedImg3.jpeg";
import leftFlower from "../../assets/leftFlower.png";
import rightFlower from "../../assets/rightFlower.png";

const AboutSection = () => {
  const weddingDate = useMemo(() => new Date("2026-04-22T00:00:00"), []);

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = weddingDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [weddingDate]);

  return (
    <section
      id="about"
      className="w-full bg-white py-16 md:py-24 px-6 relative overflow-hidden"
    >
      {/* Shimmer Animation */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: 200% 50%;
          }
          50% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 200% 50%;
          }
        }
        
        .glossy-text {
          background: linear-gradient(
            135deg,
            #C49355 0%,
            #E8B46A 80%,
            #C49355 100%
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          background-size: 200% 200%;
          animation: shimmer 4s ease-in-out infinite;
          filter: drop-shadow(0 1px 1px rgba(196, 147, 85, 0.3));
        }
        
        .glossy-text-static {
          background: linear-gradient(
            180deg,
            #F5D89A 0%,
            #E8B46A 30%,
            #C49355 70%,
            #A67B3D 100%
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          filter: drop-shadow(0 2px 2px rgba(196, 147, 85, 0.2));
        }
      `}</style>

      {/* Decorative floral elements - Right top (BEHIND content) */}
      <img
        src={rightFlower}
        alt=""
        className="absolute right-0 top-0 w-32 md:w-60 opacity-80 pointer-events-none z-0"
      />

      {/* Decorative floral elements - Left bottom (BEHIND content) */}
      <img
        src={leftFlower}
        alt=""
        className="absolute left-0 bottom-[20%] w-32 md:w-60 opacity-80 pointer-events-none z-0"
      />

      {/* Main Content (ABOVE flowers) */}
      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* Groom's Name */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl lg:text-6xl glossy-text"
        >
          Joseph Sani
        </motion.h2>

        {/* Ampersand */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-lg glossy-text-static my-8"
          style={{
            fontFamily: "'Times New Roman', Times, serif",
            fontWeight: 400,
          }}
        >
          &
        </motion.p>

        {/* Bride's Name */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl lg:text-6xl glossy-text hidden md:block"
        >
          Jennifer Iloanyionwu
        </motion.h2>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl lg:text-6xl glossy-text block md:hidden"
        >
          Jennifer <br /> Iloanyionwu
        </motion.h2>

        {/* Wedding Date */}
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-3xl md:text-5xl glossy-text mt-32"
        >
          April 22, 2026
        </motion.h3>

        {/* Countdown Timer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-4 md:gap-8 mt-6 text-gray-700"
          style={{
            fontFamily: "'Times New Roman', Times, serif",
            fontWeight: 400,
          }}
        >
          <div className="text-center">
            <span className="text-lg md:text-xl font-medium">
              {timeLeft.days}
            </span>
            <span className="text-sm md:text-base ml-1">days</span>
          </div>
          <div className="text-center">
            <span className="text-lg md:text-xl font-medium">
              {timeLeft.hours}
            </span>
            <span className="text-sm md:text-base ml-1">hours</span>
          </div>
          <div className="text-center">
            <span className="text-lg md:text-xl font-medium">
              {timeLeft.minutes}
            </span>
            <span className="text-sm md:text-base ml-1">minutes</span>
          </div>
          <div className="text-center">
            <span className="text-lg md:text-xl font-medium italic">
              {timeLeft.seconds}
            </span>
            <span className="text-sm md:text-base ml-1 italic">seconds</span>
          </div>
        </motion.div>

        {/* RSVP Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-12"
        >
          <button
            onClick={() => {
              document
                .getElementById("rsvp")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            style={{
              fontFamily: "'Times New Roman', Times, serif",
              fontWeight: 400,
            }}
            className="bg-[#3d3d3d] hover:bg-[#2d2d2d] text-white px-16 py-2 text-lg tracking-wider transition-colors duration-300 font-semibold"
          >
            RSVP
          </button>
        </motion.div>

        {/* Divider */}
        <div className="w-full h-px bg-gray-200 my-16 md:my-20"></div>

        {/* We Are Getting Married Section */}
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-3xl md:text-5xl glossy-text"
        >
          We Are Getting Married!
        </motion.h3>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-gray-600 text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed"
          style={{
            fontFamily: "'Times New Roman', Times, serif",
          }}
        >
          The Sani and Iloanyionwu family cordially invite you to celebrate the
          wedding ceremony of their beloved son and daughter.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto mt-20"
        >
          <img src={wedImg3} alt="wedding img" className="w-full h-auto" />
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
